"use client";

import React, { useEffect, useRef, useState } from "react";
import { Leaf, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function FourDecadesOfQuality() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section id="four-decades-of-quality" className="py-16 md:py-24 lg:py-32 bg-background text-foreground overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {}
          <div ref={imageRef} className={`relative w-full h-[400px] sm:h-[500px] lg:h-[600px] rounded-2xl overflow-hidden shadow-2xl transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="absolute inset-0 bg-primary/10 z-10 mix-blend-overlay"></div>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/nieser_landscaping_fleet_pittsburgh.png" alt="(Your Business Name) Landscaping team and branded trucks ready for service" className="absolute inset-0 w-full h-full object-cover object-center" />
            {}
            <div className="absolute bottom-6 left-6 z-20 bg-background/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-border flex items-center gap-4">
              <div className="bg-primary/10 p-3 rounded-full text-primary">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">Fully Licensed</p>
                <p className="text-xs text-muted-foreground">&amp; Insured In Your State</p>
              </div>
            </div>
          </div>

          {}
          <div ref={contentRef} className={`flex flex-col justify-center transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight mb-6">Why Neighbors Trust Us</h2>
            
            
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>Homeowners trust us because we focus on the details that make a property feel well cared for — clean work, dependable service, and landscaping that leaves every home looking sharper, more polished, and easier to take pride in.</p>
              
            </div>

            <Separator className="my-8 bg-border/60" />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              
              
              <Button asChild size="lg" className="w-full sm:w-auto shadow-md hover:shadow-lg transition-shadow">
                <Link to="/Get an Estimate">Get a Free Estimate</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}